import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import TagsService from '~/services/TagsService';

export const destinationContentTagOrderQueryKeys = {
  all: ['destination-content-tag-order'] as const,
  detail: (destinationId: string) => [...destinationContentTagOrderQueryKeys.all, destinationId] as const,
  locations: () => [...destinationContentTagOrderQueryKeys.all, 'locations'] as const,
};

const CONTENT_TAGS_PAGE_SIZE = 300;
const DESTINATION_ASSIGNMENTS_PAGE_SIZE = 1000;

async function getAllDestinationAssignments() {
  const assignments = [] as Awaited<ReturnType<typeof TagsService.getTagAssignments>>['result'];
  let offset = 0;

  while (true) {
    const response = await TagsService.getTagAssignments({
      entityType: 'DESTINATION',
      limit: DESTINATION_ASSIGNMENTS_PAGE_SIZE,
      offset,
    });
    assignments.push(...response.result);

    if (!response.pagination.has_next) {
      return assignments;
    }

    offset += response.pagination.limit;
  }
}

export function useGetDestinationContentTagLocationIds() {
  return useQuery({
    queryKey: destinationContentTagOrderQueryKeys.locations(),
    queryFn: async () => {
      const [{ result: tags }, assignments] = await Promise.all([
        TagsService.getTags({ limit: CONTENT_TAGS_PAGE_SIZE, offset: 0, tagType: 'content_tag' }),
        getAllDestinationAssignments(),
      ]);
      const contentTagIds = new Set(tags.map((tag) => tag.id));
      return Array.from(
        new Set(
          assignments
            .filter((assignment) => contentTagIds.has(assignment.tagId))
            .map((assignment) => assignment.entityId),
        ),
      );
    },
  });
}

export function useGetDestinationContentTags(destinationId?: string) {
  return useQuery({
    queryKey: destinationContentTagOrderQueryKeys.detail(destinationId ?? ''),
    queryFn: () => {
      if (!destinationId) {
        throw new Error('destinationId is required');
      }
      return TagsService.getDestinationContentTags(destinationId);
    },
    enabled: !!destinationId,
  });
}

export function useReorderDestinationContentTags() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ destinationId, orderedTagIds }: { destinationId: string; orderedTagIds: Array<string> }) =>
      TagsService.reorderDestinationContentTags(destinationId, orderedTagIds),
    onSuccess: (tags, { destinationId }) => {
      queryClient.setQueryData(destinationContentTagOrderQueryKeys.detail(destinationId), tags);
      enqueueSnackbar('Content Tag order saved', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Unable to save Content Tag order: ${error.message}`, { variant: 'error' });
    },
  });
}
